import type { z } from 'zod'

/**
 * A type-safe wrapper around localStorage that handles serialization/deserialization
 * and provides schema validation using Zod.
 */
export class TypedLocalStorage<T, U = T> {
  private readonly key: string
  private readonly schema: z.ZodType<T, z.ZodTypeDef, U>

  constructor(key: string, schema: z.ZodType<T, z.ZodTypeDef, U>) {
    this.key = key
    this.schema = schema
  }

  get(): T | undefined {
    try {
      const item = localStorage.getItem(this.key)
      if (!item) {
        return undefined
      }

      const parsed = JSON.parse(item)
      return this.schema.parse(parsed)
    } catch (_error) {
      // Silently handle parse/validation errors
      return undefined
    }
  }

  set(value: U): void {
    // Validate before storing
    this.schema.parse(value)
    localStorage.setItem(this.key, JSON.stringify(value))
  }

  remove(): void {
    localStorage.removeItem(this.key)
  }

  /**
   * Update the stored value using a callback function
   * @param updater - Function that receives current value (or undefined) and returns new value
   */
  update(updater: (currentValue: T | undefined) => U): void {
    const currentValue = this.get()
    const newValue = updater(currentValue)
    this.set(newValue)
  }
}

export function createTypedLocalStorage<T, U = T>(
  key: string,
  schema: z.ZodType<T, z.ZodTypeDef, U>
): TypedLocalStorage<T, U> {
  return new TypedLocalStorage(key, schema)
}
